import React, { useMemo, useState, useEffect } from "react";
import api from "../../api/api"; // Make sure this path matches your project structure
import Heading from "../../components/ui/Heading";

// Utility Functions
const parseDMY = (d) => {
  if (!d) return new Date();
  if (typeof d === "string" && d.includes("T")) {
    return new Date(d.split("T")[0]);
  }
  const [dd, mm, yyyy] = d.split("-").map(Number);
  return new Date(yyyy, mm - 1, dd);
};

const formatDMY = (date) => {
  if (!date) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const numberFormat = (n) => new Intl.NumberFormat().format(n ?? 0);

const classNames = (...arr) => arr.filter(Boolean).join(" ");

function groupBy(arr, keyFn) {
  return arr.reduce((acc, item) => {
    const k = keyFn(item);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});
}

function buildStockByItem(stock) {
  const map = {};
  for (const s of stock) {
    const key =
      (s.ItemDesc || s.ItemName || s.Partdesc || s.ItemNo || "").trim();
    map[key] = (map[key] || 0) + Number(s.StkQty || 0);
  }
  return map;
}

// Build procurement need per date per item based on cumulative demand vs stock
function buildProcurementSchedule(orders, stockByItem) {
  const byItem = groupBy(orders, (o) => o.Partdesc.trim());
  const resultRows = [];
  Object.entries(byItem).forEach(([item, rows]) => {
    const stock = Number(stockByItem[item] || 0);
    // sum demand per date for the item
    const byDate = groupBy(rows, (r) => r.DeliveryDate);
    const dates = Object.keys(byDate)
      .map((d) => ({
        d,
        date: parseDMY(d),
      }))
      .sort((a, b) => a.date - b.date);
    let cumDemand = 0;
    let cumShortage = 0;
    dates.forEach(({ d }) => {
      const todayDemand = byDate[d].reduce(
        (s, r) => s + Number(r.Qty || 0),
        0
      );
      cumDemand += todayDemand;
      const newCumShortage = Math.max(0, cumDemand - stock);
      const shortageToday = newCumShortage - cumShortage; // incremental shortage needed by this date
      cumShortage = newCumShortage;
      resultRows.push({
        DeliveryDate: d,
        Item: item,
        StockTotal: stock,
        DemandToday: todayDemand,
        DemandCumulative: cumDemand,
        ShortageToday: Math.max(0, shortageToday),
        ShortageCumulative: newCumShortage,
      });
    });
  });
  resultRows.sort(
    (a, b) =>
      parseDMY(a.DeliveryDate) -
        parseDMY(b.DeliveryDate) || a.Item.localeCompare(b.Item)
  );
  return resultRows;
}

function toCSV(rows) {
  if (!rows || rows.length === 0) return "";
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
  };
  return [
    cols.join(","),
    ...rows.map((r) => cols.map((c) => esc(r[c])).join(",")),
  ].join("\n");
}

function download(filename, content, mime = "text/csv;charset=utf-8;") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function SectionCard({ title, children, className }) {
  return (
    <div
      className={classNames(
        "rounded-2xl shadow-sm p-4 md:p-6 bg-white/80 backdrop-blur border border-slate-200",
        className
      )}
    >
      <div className="flex items-center gap-2 mb-3 text-lg font-semibold md:text-xl">
        <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-500" />
        {title}
      </div>
      {children}
    </div>
  );
}

export default function OrderStockReport() {
  const [query, setQuery] = useState("");
  const [view, setView] = useState("summary");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [apiOrders, setApiOrders] = useState([]);
  const [apiStock, setApiStock] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch API Data for orders and stock upon mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Use the correct API endpoint and user variable here
        // Replace "demoUser" with authenticated user if available
        const apiData = await api.ORderVsStock_API("demoUser");
        const { Table = [], Table1 = [] } = apiData;
        setApiStock(Table);
        setApiOrders(
          Table1.map((row) => ({
            ...row,
            DeliveryDate: row.DeliveryDate
              ? formatDMY(parseDMY(row.DeliveryDate))
              : "",
            Partdesc: row.Partdesc ? row.Partdesc.trim() : "",
            Vname: row.Vname ? row.Vname.trim() : "",
            Qty: Number(row.Qty || 0),
          }))
        );
      } catch (err) {
        console.error("Failed to load stock/orders:", err);
        setApiStock([]);
        setApiOrders([]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Build stock totals by item name (summed across godowns)
  const stockByItem = useMemo(() => buildStockByItem(apiStock), [apiStock]);

  // FILTERS
  const filteredOrders = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apiOrders
      .filter((o) =>
        q
          ? [
              o.Vname,
              o.Partdesc,
              String(o.PoId),
              String(o.MobileNo),
              o.DeliveryDate,
            ].some((v) => String(v || "").toLowerCase().includes(q))
          : true
      )
      .filter((o) =>
        dateFrom ? parseDMY(dateFrom) <= parseDMY(o.DeliveryDate) : true
      )
      .filter((o) =>
        dateTo ? parseDMY(o.DeliveryDate) <= parseDMY(dateTo) : true
      );
  }, [query, dateFrom, dateTo, apiOrders]);

  // Main summary: procurement plan
  const procurementRows = useMemo(
    () => buildProcurementSchedule(filteredOrders, stockByItem),
    [filteredOrders, stockByItem]
  );

  // Customer summary
  const byCustomer = useMemo(() => {
    const grp = groupBy(filteredOrders, (o) => o.Vname);
    const rows = [];
    Object.entries(grp).forEach(([cust, list]) => {
      const byItem = groupBy(list, (x) => x.Partdesc);
      Object.entries(byItem).forEach(([item, arr]) => {
        const qty = arr.reduce((s, r) => s + Number(r.Qty || 0), 0);
        rows.push({
          Customer: cust,
          Item: item,
          TotalQty: qty,
          Orders: arr.length,
        });
      });
    });
    rows.sort(
      (a, b) =>
        a.Customer.localeCompare(b.Customer) || a.Item.localeCompare(b.Item)
    );
    return rows;
  }, [filteredOrders]);

  // Item summary
  const byItemSummary = useMemo(() => {
    const grp = groupBy(filteredOrders, (o) => o.Partdesc);
    const rows = [];
    Object.entries(grp).forEach(([item, list]) => {
      const qty = list.reduce((s, r) => s + Number(r.Qty || 0), 0);
      rows.push({
        Item: item,
        TotalOrdered: qty,
        StockTotal: Number(stockByItem[item] || 0),
        ShortageIfToday: Math.max(0, qty - Number(stockByItem[item] || 0)),
      });
    });
    rows.sort((a, b) => a.Item.localeCompare(b.Item));
    return rows;
  }, [filteredOrders, stockByItem]);

  // Order details (with stock context)
  const detailsRows = useMemo(() => {
    const rows = filteredOrders.map((o) => ({
      Date: o.DeliveryDate,
      PoId: o.PoId,
      Customer: o.Vname,
      Mobile: o.MobileNo,
      Item: o.Partdesc,
      Qty: o.Qty,
      StockTotal: Number(stockByItem[o.Partdesc] || 0),
      RowShortageIfAlone: Math.max(
        0,
        Number(o.Qty) - Number(stockByItem[o.Partdesc] || 0)
      ),
    }));
    rows.sort(
      (a, b) =>
        parseDMY(a.Date) -
          parseDMY(b.Date) ||
        a.Customer.localeCompare(b.Customer) ||
        a.Item.localeCompare(b.Item)
    );
    return rows;
  }, [filteredOrders, stockByItem]);

  // EXPORT/SHARE
  const exportCurrentToCSV = () => {
    let rows = [];
    if (view === "summary") rows = procurementRows;
    if (view === "byCustomer") rows = byCustomer;
    if (view === "byItem") rows = byItemSummary;
    if (view === "details") rows = detailsRows;
    const csv = toCSV(rows);
    const fn = `report_${view}_${Date.now()}.csv`;
    download(fn, csv);
  };

  const buildWhatsAppText = () => {
    const lines = ["Order vs Stock – Procurement Plan", ""];
    const byDate = groupBy(procurementRows, (r) => r.DeliveryDate);
    Object.keys(byDate)
      .sort((a, b) => parseDMY(a) - parseDMY(b))
      .forEach((d) => {
        lines.push(`📅 ${d}`);
        byDate[d]
          .filter((r) => (r.ShortageToday || 0) > 0)
          .forEach((r) => lines.push(`• ${r.Item}: need ${r.ShortageToday}`));
        const noShort = byDate[d].every((r) => (r.ShortageToday || 0) === 0);
        if (noShort) lines.push("• All items covered from stock");
        lines.push("");
      });
    return lines.join("\n");
  };

  const shareOnWhatsApp = () => {
    const text = buildWhatsAppText();
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const shareViaEmail = () => {
    const subject = "Order vs Stock – Procurement Plan";
    const body = buildWhatsAppText();
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const HeaderStat = ({ label, value, hint }) => (
    <div className="flex-1 p-4 border rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 md:p-5">
      <div className="text-xs tracking-wide uppercase text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold md:text-3xl">
        {numberFormat(value)}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );

  const totalOrders = filteredOrders.length;
  const totalQty = filteredOrders.reduce((s, r) => s + Number(r.Qty || 0), 0);
  const totalShortage = procurementRows.reduce(
    (s, r) => s + Number(r.ShortageToday || 0),
    0
  );

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-white to-sky-50 text-slate-800">
      <div className="px-3 py-4 mx-auto max-w-7xl sm:px-4 md:px-6 md:py-8">
        <div className="flex flex-col gap-4 mb-5 md:flex-row md:items-end md:gap-6">
          <div className="flex-1">
            <h1 className="w-full text-center mb-6 text-2xl sm:text-3xl font-bold text-gray-800">
              Order vs Stock Availability
            </h1>
            <p className="mt-1 text-sm md:text-base text-slate-600">
              Date-wise • Customer-wise • Item-wise • Procurement per date
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportCurrentToCSV}
              className="px-3 py-2 text-white bg-indigo-600 rounded-xl hover:opacity-90 active:scale-95"
            >
              Export CSV
            </button>
            <button
              onClick={shareViaEmail}
              className="px-3 py-2 text-white rounded-xl bg-sky-600 hover:opacity-90 active:scale-95"
            >
              Mail
            </button>
            <button
              onClick={shareOnWhatsApp}
              className="px-3 py-2 text-white rounded-xl bg-emerald-600 hover:opacity-90 active:scale-95"
            >
              WhatsApp
            </button>
          </div>
        </div>
        {/* Filters */}
        <SectionCard
          title="Filters"
          className="mb-5"
          children={
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="md:col-span-2">
                <input
                  type="text"
                  placeholder="Search PoId / Customer / Item"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full px-3 py-2 border outline-none rounded-xl focus:ring-4 focus:ring-fuchsia-200"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-16 text-sm text-slate-600">From</label>
                <input
                  type="text"
                  placeholder="DD-MM-YYYY"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-xl focus:ring-4 focus:ring-indigo-200"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-16 text-sm text-slate-600">To</label>
                <input
                  type="text"
                  placeholder="DD-MM-YYYY"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-xl focus:ring-4 focus:ring-indigo-200"
                />
              </div>
            </div>
          }
        />
        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 mb-5 md:grid-cols-3 md:gap-4">
          <HeaderStat label="Orders" value={totalOrders} />
          <HeaderStat label="Total Qty Ordered" value={totalQty} />
          <HeaderStat
            label="Total Procurement Needed"
            value={totalShortage}
            hint="Cumulative shortage across dates"
          />
        </div>
        {/* View Switcher */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { id: "summary", name: "Procurement by Date & Item" },
            { id: "details", name: "Order Details" },
            { id: "byCustomer", name: "Customer-wise" },
            { id: "byItem", name: "Item-wise" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              className={classNames(
                "px-3 py-2 rounded-xl border",
                view === t.id
                  ? "bg-fuchsia-600 text-white border-fuchsia-600"
                  : "bg-white hover:bg-slate-50"
              )}
            >
              {t.name}
            </button>
          ))}
        </div>
        {/* Tables */}
        {loading ? (
          <div className="py-8 text-center text-slate-400">Loading...</div>
        ) : (
          <>
            {view === "summary" && (
              <SectionCard
                title="Procurement plan (date-wise, item-wise)"
                children={
                  <div className="overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="sticky top-0 text-white bg-gradient-to-r from-indigo-600 to-fuchsia-600">
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-right">
                            Demand (today)
                          </th>
                          <th className="px-3 py-2 text-right">
                            Demand (cum)
                          </th>
                          <th className="px-3 py-2 text-right">Stock</th>
                          <th className="px-3 py-2 text-right">
                            Procure (today)
                          </th>
                          <th className="px-3 py-2 text-right">
                            Shortage (cum)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {procurementRows.map((r, i) => (
                          <tr
                            key={i}
                            className="odd:bg-white even:bg-slate-50"
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.DeliveryDate}
                            </td>
                            <td className="px-3 py-2">{r.Item}</td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.DemandToday)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.DemandCumulative)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.StockTotal)}
                            </td>
                            <td
                              className={classNames(
                                "px-3 py-2 text-right font-semibold",
                                r.ShortageToday > 0
                                  ? "text-rose-600"
                                  : "text-emerald-600"
                              )}
                            >
                              {numberFormat(r.ShortageToday)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.ShortageCumulative)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                }
              />
            )}
            {view === "byCustomer" && (
              <SectionCard
                title="Customer-wise summary"
                children={
                  <div className="overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="sticky top-0 text-white bg-gradient-to-r from-emerald-600 to-teal-600">
                          <th className="px-3 py-2 text-left">Customer</th>
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-right">Total Qty</th>
                          <th className="px-3 py-2 text-right">Orders</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byCustomer.map((r, i) => (
                          <tr
                            key={i}
                            className="odd:bg-white even:bg-slate-50"
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.Customer}
                            </td>
                            <td className="px-3 py-2">{r.Item}</td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.TotalQty)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.Orders)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                }
              />
            )}
            {view === "byItem" && (
              <SectionCard
                title="Item-wise summary"
                children={
                  <div className="overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="sticky top-0 text-white bg-gradient-to-r from-amber-600 to-orange-600">
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-right">
                            Total Ordered
                          </th>
                          <th className="px-3 py-2 text-right">Stock</th>
                          <th className="px-3 py-2 text-right">
                            Shortage if fulfilled today
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {byItemSummary.map((r, i) => (
                          <tr
                            key={i}
                            className="odd:bg-white even:bg-slate-50"
                          >
                            <td className="px-3 py-2">{r.Item}</td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.TotalOrdered)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.StockTotal)}
                            </td>
                            <td
                              className={classNames(
                                "px-3 py-2 text-right font-semibold",
                                r.ShortageIfToday > 0
                                  ? "text-rose-600"
                                  : "text-emerald-600"
                              )}
                            >
                              {numberFormat(r.ShortageIfToday)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                }
              />
            )}
            {view === "details" && (
              <SectionCard
                title="Order details (with stock context)"
                children={
                  <div className="overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="sticky top-0 text-white bg-gradient-to-r from-slate-700 to-slate-900">
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-right">PoId</th>
                          <th className="px-3 py-2 text-left">Customer</th>
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">
                            Stock (total)
                          </th>
                          <th className="px-3 py-2 text-right">
                            Row shortage (if alone)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsRows.map((r, i) => (
                          <tr
                            key={i}
                            className="odd:bg-white even:bg-slate-50"
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.Date}
                            </td>
                            <td className="px-3 py-2 text-right">{r.PoId}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.Customer}
                            </td>
                            <td className="px-3 py-2">{r.Item}</td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.Qty)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {numberFormat(r.StockTotal)}
                            </td>
                            <td
                              className={classNames(
                                "px-3 py-2 text-right font-semibold",
                                r.RowShortageIfAlone > 0
                                  ? "text-rose-600"
                                  : "text-emerald-600"
                              )}
                            >
                              {numberFormat(r.RowShortageIfAlone)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                }
              />
            )}
            <div className="mt-6 text-xs text-slate-500">
              End
            </div>
          </>
        )}
      </div>
    </div>
  );
}